interface ResolveLuxPlusMemberPriceParams {
  metadataLowestMemberPrice?: number
  referencePrice?: number
  packagePrice?: number
  packageMemberPrice?: number
  fallbackMemberPricingPrice?: number
  fallbackMemberPrice?: number
}

/**
 * Resolves a LuxPlus member price when metadata lowestMemberPrice is missing.
 *
 * Fallback chain:
 *   1. metadata lowestMemberPrice (if > 0)
 *   2. estimate from package-level discount applied to referencePrice
 *   3. fallback memberPricing.price
 *   4. fallback memberPrice
 */
export default function resolveLuxPlusMemberPrice({
  metadataLowestMemberPrice,
  referencePrice,
  packagePrice,
  packageMemberPrice,
  fallbackMemberPricingPrice,
  fallbackMemberPrice,
}: ResolveLuxPlusMemberPriceParams): number | undefined {
  const metadataMemberPrice = (metadataLowestMemberPrice ?? 0) > 0
    ? metadataLowestMemberPrice
    : undefined
  if (metadataMemberPrice !== undefined) {
    return metadataMemberPrice
  }

  const hasValidPackageMemberDiscount =
    (packageMemberPrice ?? 0) > 0 &&
    (packagePrice ?? 0) > 0 &&
    !!packagePrice &&
    !!packageMemberPrice &&
    packageMemberPrice < packagePrice

  const packageMemberDiscount = hasValidPackageMemberDiscount
    ? packagePrice - packageMemberPrice
    : undefined

  const safeReferencePrice = referencePrice ?? 0
  const estimateNeedsRoundingAdjustment =
    packageMemberDiscount !== undefined &&
    !!packagePrice &&
    safeReferencePrice > packagePrice

  const estimatedMemberPriceFromPackageDiscount =
    packageMemberDiscount !== undefined && safeReferencePrice > 0
      ? Math.max(
          safeReferencePrice - packageMemberDiscount + (estimateNeedsRoundingAdjustment ? 1 : 0),
          0,
        )
      : undefined

  return estimatedMemberPriceFromPackageDiscount
    ?? fallbackMemberPricingPrice
    ?? fallbackMemberPrice
}
